import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Upload, FileText, Briefcase, ChevronRight, AlertCircle, Shield } from 'lucide-react'
import useStore from '../store'

const INDUSTRY_OPTIONS = [
  { industry: '金融央企', positions: ['银行管培生', '证券分析师', '保险精算', '风控专员', '客户经理'] },
  { industry: '能源央企', positions: ['电力工程师', '新能源技术', '石油化工', '安全环保', '项目管理'] },
  { industry: '通信央企', positions: ['网络工程师', '产品经理', '数据分析', '市场营销', '技术支持'] },
  { industry: '建筑央企', positions: ['土木工程师', '造价工程师', '设计管理', '施工管理', 'BIM工程师'] },
  { industry: '制造央企', positions: ['机械工程师', '自动化工程师', '质量管理', '生产管理', '研发工程师'] },
  { industry: '交通央企', positions: ['轨道交通', '航空管理', '物流管理', '航运管理', '智能交通'] }
]

const PrecisionMatchPage = () => {
  const navigate = useNavigate()
  const { assessmentData, setPrecisionReport } = useStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [selectedIndustry, setSelectedIndustry] = useState('')
  const [selectedPosition, setSelectedPosition] = useState('')
  const [customPosition, setCustomPosition] = useState('')
  const [useCustomPosition, setUseCustomPosition] = useState(false)
  const [errors, setErrors] = useState({})
  const fileInputRef = useRef(null)

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
    if (!allowedTypes.includes(file.type)) {
      setErrors({ file: '请上传PDF或Word格式的简历' })
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors({ file: '文件大小不能超过10MB' })
      return
    }
    setUploadedFile(file)
    setErrors({})
  }

  const validateForm = () => {
    const newErrors = {}
    if (!uploadedFile) newErrors.file = '请上传简历'
    if (useCustomPosition) {
      if (!customPosition.trim()) newErrors.position = '请输入目标岗位'
    } else {
      if (!selectedIndustry) newErrors.industry = '请选择行业'
      if (!selectedPosition) newErrors.position = '请选择岗位'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const generatePrecisionReport = (data) => {
    const dimensions = {
      hardConditions: { score: Math.floor(Math.random() * 30) + 60, label: '硬性条件' },
      practice: { score: Math.floor(Math.random() * 40) + 50, label: '实践经历' },
      club: { score: Math.floor(Math.random() * 50) + 40, label: '社团活动' },
      family: { score: Math.floor(Math.random() * 60) + 30, label: '家庭背景' },
      location: { score: Math.floor(Math.random() * 40) + 50, label: '地域匹配' },
      political: { score: Math.floor(Math.random() * 50) + 40, label: '政治素养' },
      stability: { score: Math.floor(Math.random() * 40) + 50, label: '稳定性' },
      culture: { score: Math.floor(Math.random() * 50) + 30, label: '文化匹配' }
    }
    const totalScore = Math.round(
      Object.values(dimensions).reduce((sum, d) => sum + d.score, 0) / Object.keys(dimensions).length
    )
    const aiComments = [
      `你的简历在${data.targetIndustry || '央国企'}HR眼中，第一轮就会因为缺乏与岗位直接相关的实习经历被筛掉。现在很多央国企都在使用AI筛简历，你的关键词匹配度只有${Math.floor(Math.random() * 30) + 20}%。`,
      `你的专业背景有一定优势，但简历描述过于互联网化。央国企HR看到主导项目、赋能团队这类词汇会直接减分，他们更想看到主持工作、配合团队这样的表述。`,
      `你的学校层次在目标岗位的竞争者中处于中等水平，但你的实践经历描述不够具体。建议用STAR法则重新梳理，突出你在团队中的实际贡献。`
    ]
    const suggestions = [
      { title: '简历关键词优化', content: '将互联网黑话替换为央国企常用表述，如主导→主持，赋能→支持', priority: '高' },
      { title: '补充相关实习经历', content: '建议寻找与目标岗位相关的实习，哪怕是短期项目经历也能显著提升匹配度', priority: '高' },
      { title: '政治素养提升', content: '参加党课学习、志愿服务活动，提升政治素养表达', priority: '中' },
      { title: '面试准备', content: '重点准备价值观类问题，如如何看待集体利益和个人利益的关系', priority: '中' }
    ]
    const badWords = ['主导', '赋能', '沉淀', '抓手', '闭环', '底层逻辑']
    const foundBadWords = badWords.filter(() => Math.random() > 0.5)
    return {
      totalScore, dimensions,
      aiComment: aiComments[Math.floor(Math.random() * aiComments.length)],
      suggestions,
      foundBadWords: foundBadWords.length > 0 ? foundBadWords : ['主导', '赋能'],
      targetPosition: data.targetPosition,
      generatedAt: new Date().toISOString()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      const report = generatePrecisionReport({
        ...assessmentData,
        resume: uploadedFile?.name,
        targetPosition: useCustomPosition ? customPosition : selectedPosition,
        targetIndustry: selectedIndustry
      })
      setPrecisionReport(report)
      await new Promise(resolve => setTimeout(resolve, 2000))
      navigate('/report')
    } catch (error) {
      setErrors({ submit: '报告生成失败，请稍后重试' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const currentIndustry = INDUSTRY_OPTIONS.find(i => i.industry === selectedIndustry)

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
        <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
          <Shield size={16} className="mr-2" />已支付成功，解锁精准匹配
        </div>
        <h1 className="section-title">精准匹配测评</h1>
        <p className="section-subtitle">上传简历，选择目标岗位，获取AI精准分析报告</p>
      </motion.div>

      <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        onSubmit={handleSubmit} className="card p-6 md:p-8">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">上传简历 <span className="text-red-500">*</span></label>
          <div onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${uploadedFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'}`}>
            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" />
            {uploadedFile ? (
              <div className="flex items-center justify-center space-x-2">
                <FileText className="text-green-600" size={24} />
                <span className="text-green-700 font-medium">{uploadedFile.name}</span>
              </div>
            ) : (
              <>
                <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-gray-600">点击上传简历（PDF/Word）</p>
                <p className="text-xs text-gray-400 mt-1">支持PDF、DOC、DOCX格式，最大10MB</p>
              </>
            )}
          </div>
          {errors.file && <p className="text-red-500 text-sm mt-2">{errors.file}</p>}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">第一目标岗位 <span className="text-red-500">*</span></label>
          {!useCustomPosition ? (
            <>
              <select value={selectedIndustry} onChange={(e) => { setSelectedIndustry(e.target.value); setSelectedPosition('') }} className="input-field mb-3">
                <option value="">选择行业</option>
                {INDUSTRY_OPTIONS.map(ind => <option key={ind.industry} value={ind.industry}>{ind.industry}</option>)}
              </select>
              {currentIndustry && (
                <select value={selectedPosition} onChange={(e) => setSelectedPosition(e.target.value)} className="input-field">
                  <option value="">选择岗位</option>
                  {currentIndustry.positions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                </select>
              )}
            </>
          ) : (
            <input type="text" value={customPosition} onChange={(e) => setCustomPosition(e.target.value)} placeholder="请输入目标岗位名称" className="input-field" />
          )}
          <div className="mt-2">
            <button type="button" onClick={() => { setUseCustomPosition(!useCustomPosition); setSelectedIndustry(''); setSelectedPosition(''); setCustomPosition('') }}
              className="text-sm text-primary-600 hover:underline">
              {useCustomPosition ? '从列表中选择' : '手动输入岗位'}
            </button>
          </div>
          {errors.position && <p className="text-red-500 text-sm mt-2">{errors.position}</p>}
        </div>

        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="text-yellow-600 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">敏感信息处理说明</p>
              <p>系统可能分析简历中的家庭背景、住址等信息，仅用于匹配度评估。</p>
              <p className="mt-1">所有敏感数据采用AES-256加密存储，分析结果标注仅供参考。</p>
            </div>
          </div>
        </div>

        <motion.button type="submit" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          className="w-full btn-primary text-lg py-4 flex items-center justify-center">
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              AI分析中，约需10秒...
            </span>
          ) : (
            <span className="flex items-center">
              <Briefcase size={20} className="mr-2" />生成精准匹配报告<ChevronRight size={20} className="ml-2" />
            </span>
          )}
        </motion.button>
        {errors.submit && <p className="text-red-500 text-center mt-4">{errors.submit}</p>}
      </motion.form>
    </div>
  )
}

export default PrecisionMatchPage
